// src/pages/StockInPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { apiUrl } from "../config/api";

/** ---------- Types ---------- */
type Product = {
  code: string;
  name: string;
  price: number;
  unit?: string;
  imageUrl?: string;
};

type MovementPayload = {
  product_code: string;
  qty: number;
  unit_cost: number;
  base_unit_cost: number;
  keychain_option_id: string | null;
  accessory_ids: AccessoryUsage[] | null; // ⬅️ เก็บเป็น array ของ object แทน
  reason: "PURCHASE";
  ref_table: "purchase";
  ref_no: string;
  location_id: number;
};

type AccessoryUsage = {
  id: string;
  qtyPerUnit: number;       // ใช้กี่ชิ้นต่อสินค้า 1 ตัว
  extraCostPerUnit: number; // ทุนของเครื่องประดับ 1 ชิ้น
};

type KeychainOption = {
  id: string;          // เก็บเป็น string ไปเลย แม้ใน DB จะเป็น number
  label: string;
  extraCost: number;   // มาจาก extra_cost
  imageUrl?: string;
  stockOnHand?: number;
};

type AccessoryOption = {
  id: string;
  label: string;
  extraCost: number;
  imageUrl?: string;
  stockOnHand?: number;
};

export default function StockInPage(): JSX.Element {
  /** ---------- UI State ---------- */
  const [productsByCode, setProductsByCode] = useState<Map<string, Product>>(new Map());
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productsError, setProductsError] = useState<string | null>(null);

  const [productCode, setProductCode] = useState("");
  const [qty, setQty] = useState<number | "">("");
  const [unitCost, setUnitCost] = useState<number | "">(""); // ต้นทุนสินค้าต่อหน่วย (ไม่รวมพวงกุญแจ)
  const [refNo, setRefNo] = useState("");
  const [locationId, setLocationId] = useState<number>(1); // 1=MAIN, 2=SHOP, 3=TIKTOK, 4=USA
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");

  const [accessoryOptions, setAccessoryOptions] = useState<AccessoryOption[]>([]);
  const [useAccessory, setUseAccessory] = useState<boolean>(false);
  const [selectedAccessoryIds, setSelectedAccessoryIds] = useState<string[]>([]);
  // จำนวนของเครื่องประดับแต่ละแบบต่อสินค้า 1 ชิ้น
  const [accessoryQtyById, setAccessoryQtyById] = useState<Record<string, number>>({});

  // modal เครื่องประดับ
  const [showAccessoryModal, setShowAccessoryModal] = useState<boolean>(false);
  const [accessoryQuery, setAccessoryQuery] = useState<string>("");

  const [toast, setToast] = useState<{
    show: boolean;
    message: string;
    type?: "success" | "warning" | "info" | "danger";
  }>({
    show: false,
    message: "",
    type: "success",
  });

   /** ---------- ตัวเลือกพวงกุญแจ ---------- */
   const [keychainOptions, setKeychainOptions] = useState<KeychainOption[]>([]);
   const [loadingKeychains, setLoadingKeychains] = useState(false);
   const [keychainError, setKeychainError] = useState<string | null>(null);
 
   const [keychainId, setKeychainId] = useState<string>("");   // เดี๋ยวให้ default จาก DB

   

  /** ---------- ตัวเลือกพวงกุญแจ ---------- */

  const [useKeychain, setUseKeychain] = useState<boolean>(true);

  // modal สำหรับเลือกพวงกุญแจ
  const [showKeychainModal, setShowKeychainModal] = useState<boolean>(false);
  const [keychainQuery, setKeychainQuery] = useState<string>("");

  const selectedKeychain = useMemo<KeychainOption>(() => {
    return (
      keychainOptions.find((k) => k.id === keychainId) ??
      keychainOptions[0] ?? {
        id: "",
        label: "ไม่ใช้พวงกุญแจ",
        extraCost: 0,
      }
    );
  }, [keychainId, keychainOptions]);

  const filteredKeychainOptions = useMemo<KeychainOption[]>(() => {
    const q = keychainQuery.trim().toLowerCase();
    const list = keychainOptions;
    if (!q) return list;
    return list.filter((k) => k.label.toLowerCase().includes(q));
  }, [keychainQuery, keychainOptions]);

  // ต้นทุนพวงกุญแจต่อชิ้น
  const keychainUnitCost = selectedKeychain.extraCost;

  /** ---------- Accessories logic ---------- */
  // accessories ที่ค้นหาแล้ว
  const filteredAccessoryOptions = useMemo<AccessoryOption[]>(() => {
    const q = accessoryQuery.trim().toLowerCase();
    const list = accessoryOptions;
    if (!q) return list;
    return list.filter((a) => a.label.toLowerCase().includes(q));
  }, [accessoryQuery, accessoryOptions]);

  // list accessories ที่ถูกเลือก
  const selectedAccessories = useMemo<AccessoryOption[]>(() => {
    if (!selectedAccessoryIds.length) return [];
    const setIds = new Set(selectedAccessoryIds);
    return accessoryOptions.filter((a) => setIds.has(a.id));
  }, [selectedAccessoryIds, accessoryOptions]);

  // ต้นทุนรวมของเครื่องประดับต่อชิ้น (รวมทุกอันที่เลือก)
  const accessoryExtraCost = useMemo(() => {
    return selectedAccessories.reduce((sum, a) => {
      return sum + a.extraCost;
    }, 0);
  }, [selectedAccessories]);

  // รวมต้นทุนต่อหน่วย = สินค้า + พวงกุญแจ
  const finalUnitCost = useMemo(() => {
    const base = unitCost === "" ? 0 : Number(unitCost);
    const extraKeychain = useKeychain ? keychainUnitCost : 0;
    const extraAccessory = useAccessory ? accessoryExtraCost : 0;
    return base + extraKeychain + extraAccessory;
  }, [unitCost, keychainUnitCost, useKeychain, useAccessory, accessoryExtraCost]);

  // modal เลือกสินค้าเดิม
  const [showProductsModal, setShowProductsModal] = useState<boolean>(false);
  const [productQuery, setProductQuery] = useState<string>("");
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  /** ---------- Helpers ---------- */
  const selectedProduct = useMemo(() => {
    const key = productCode.trim();
    return productsByCode.get(key) ?? productsByCode.get(key.toLowerCase()) ?? null;
  }, [productCode, productsByCode]);

  // รวมเป็นรายการสินค้าทั้งหมด (unique by code + sort)
  const allProducts = useMemo(() => {
    const seen = new Set<string>();
    const arr: Product[] = [];
    for (const p of Array.from(productsByCode.values())) {
      if (seen.has(p.code)) continue;
      seen.add(p.code);
      arr.push(p);
    }
    return arr.sort(naturalCodeCompare);
  }, [productsByCode]);

  // popup แสดงข้อมูล product ทั้งหมด
  const filteredProducts = useMemo<Product[]>(() => {
    const q = productQuery.trim().toLowerCase();
    if (!q) return allProducts;
    return allProducts.filter(
      (p) =>
        p.code.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        (p.unit?.toLowerCase() ?? "").includes(q)
    );
  }, [productQuery, allProducts]);

  

  const openProductsModal = (): void => {
    setProductQuery("");
    setShowProductsModal(true);
  };

  const closeProductsModal = (): void => setShowProductsModal(false);

  const chooseProduct = (p: Product): void => {
    setProductCode(p.code);
    setShowProductsModal(false);
    // โฟกัสไปช่อง qty ให้ flow ไว
    setTimeout(() => (document.getElementById("qty") as HTMLInputElement | null)?.focus(), 0);
  };

  /** ---------- Keychain modal helpers ---------- */
  const openKeychainModal = (): void => {
    setKeychainQuery("");
    setShowKeychainModal(true);
  };

  const openAccessoryModal = (): void => {
    setAccessoryQuery("");
    setShowAccessoryModal(true);
  };
  
  const closeAccessoryModal = (): void => {
    setShowAccessoryModal(false);
  };
  
  const toggleAccessory = (opt: AccessoryOption): void => {
    setSelectedAccessoryIds((prev) => {
      if (prev.includes(opt.id)) {
        // unselect → เอาออกจาก list + map qty
        setAccessoryQtyById((prevQty) => {
          const { [opt.id]: _removed, ...rest } = prevQty;
          return rest;
        });
        return prev.filter((id) => id !== opt.id);
      }
  
      // select → ตั้งค่า qty default = 1 ถ้ายังไม่มี
      setAccessoryQtyById((prevQty) => ({
        ...prevQty,
        [opt.id]: prevQty[opt.id] && prevQty[opt.id] > 0 ? prevQty[opt.id] : 1,
      }));
      return [...prev, opt.id];
    });
  };

  const setAccessoryQty = (id: string, raw: number): void => {
    const qty = Number.isFinite(raw) ? Math.max(1, Math.floor(raw)) : 1;
    setAccessoryQtyById((prev) => ({
      ...prev,
      [id]: qty,
    }));
  };

  const closeKeychainModal = (): void => {
    setShowKeychainModal(false);
  };

  const chooseKeychain = (opt: KeychainOption): void => {
    setKeychainId(opt.id);
    setShowKeychainModal(false);
  };

  /** auto-hide toast */
  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast({ show: false, message: "" }), 2200);
    return () => clearTimeout(t);
  }, [toast.show]);
    
  /** ---------- Load products ---------- */
  const loadProducts = async (): Promise<void> => {
    try {
      setLoadingProducts(true);
      setProductsError(null);

      const resp = await fetch(apiUrl("/products"));
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.error ?? resp.statusText);

      const list: Product[] = (json.items ?? []).map((r: any) => ({
        code: r.code,
        name: r.name,
        price: Number(r.price ?? 0),
        unit: r.unit ?? undefined,
        imageUrl: r.imageUrl ?? undefined,
      }));

      const map = new Map<string, Product>();
      for (const p of list) {
        map.set(p.code, p);
        map.set(p.code.toLowerCase(), p); // เผื่อพิมพ์เล็ก/ใหญ่
      }
      setProductsByCode(map);

      setToast({ show: true, message: "อัปเดตรายการสินค้าแล้ว", type: "success" });
    } catch (e: any) {
      const msg = e?.message ?? "ดึงรายการสินค้าไม่สำเร็จ";
      setProductsError(msg);
      setToast({ show: true, message: msg, type: "danger" });
    } finally {
      setLoadingProducts(false);
    }
  };

  /** ---------- Load KeychainOptions ---------- */
  const loadKeychainOptions = async (): Promise<void> => {
    try {
      setLoadingKeychains(true);
      setKeychainError(null);
  
      const resp = await fetch(apiUrl("/keychain_options_list"));
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.error ?? resp.statusText);
  
      const rows = Array.isArray(json.items) ? json.items : [];
  
      const kcList: KeychainOption[] = [];
      const acList: AccessoryOption[] = [];
  
      rows.forEach((r: any) => {
        const lastUnit =
          r.last_unit_cost !== undefined && r.last_unit_cost !== null
            ? Number(r.last_unit_cost)
            : null;
        const baseExtra = Number(r.extra_cost ?? r.extraCost ?? 0);
        const finalExtra =
          lastUnit !== null && Number.isFinite(lastUnit) ? lastUnit : baseExtra;
  
        const common = {
          id: String(r.id),
          label: String(r.label ?? ""),
          extraCost: finalExtra,
          imageUrl: r.imageUrl ?? r.image_url ?? undefined,
          stockOnHand:
            r.stock_on_hand !== undefined && r.stock_on_hand !== null
              ? Number(r.stock_on_hand)
              : undefined,
        };
  
        if (!r.category || r.category === "KEYCHAIN") {
          kcList.push(common);
        }
        if (r.category === "ACCESSORY") {
          acList.push(common);
        }
      });
  
      kcList.sort((a, b) => Number(a.id) - Number(b.id));
      acList.sort((a, b) => Number(a.id) - Number(b.id));
  
      setKeychainOptions(kcList);
      setAccessoryOptions(acList);
  
      const noneOpt = kcList.find((o) => o.label === "ไม่ใช้พวงกุญแจ") ?? kcList[0];
      if (noneOpt) {
        setKeychainId((prev) =>
          prev && kcList.some((o) => o.id === prev) ? prev : noneOpt.id
        );
      }
    } catch (e: any) {
      const msg = e?.message ?? "โหลดรายการพวงกุญแจไม่สำเร็จ";
      setKeychainError(msg);
      setToast({ show: true, message: msg, type: "danger" });
    } finally {
      setLoadingKeychains(false);
    }
  };

   /** ---------- โหลดข้อมูลมาสเตอร์สินค้าหลัก + พวงกุญแจ + เครื่องประดับ ---------- */
   const refreshMasters = async (): Promise<void> => {
    // จะให้รันคู่กันหรือทีละอันก็ได้
    await Promise.all([loadProducts(), loadKeychainOptions()]);
  };


  /** โหลดข้อมูลครั้งแรก: สินค้า + พวงกุญแจ + เครื่องประดับ */
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!alive) return;
      await refreshMasters();
    })();
    return () => {
      alive = false;
    };
  }, []);

  const resetForm = (): void => {
    setProductCode("");
    setQty("");
    setUnitCost("");
    setRefNo("");
    setErrorMsg("");
  
    setUseKeychain(true);
  
    setUseAccessory(false);
    setSelectedAccessoryIds([]);
    setAccessoryQtyById({}); // ⬅️ ล้างจำนวนเครื่องประดับ
  
    const noneOpt =
      keychainOptions.find((o) => o.label === "ไม่ใช้พวงกุญแจ") ??
      keychainOptions[0];
    setKeychainId(noneOpt ? noneOpt.id : "");
  
    setUseKeychain(true);
  };

  const validate = (): string | null => {
    if (!productCode.trim()) return "กรุณาเลือกรหัสสินค้า";
    if (!selectedProduct) return `ไม่พบสินค้า: ${productCode}`;
    if (qty === "" || Number(qty) <= 0) return "จำนวนต้องมากกว่า 0";
    if (unitCost === "" || Number(unitCost) < 0) return "ต้นทุนต่อหน่วยต้องเป็นเลข 0 ขึ้นไป";
    return null;
  };

  /** ---------- Submit ---------- */
  // PMN-CRITICAL(/StockInPage): creates real inventory lots and can consume keychain/accessory FIFO stock in the backend transaction.
  const submit = async (): Promise<void> => {
    setErrorMsg("");
    const invalid = validate();
    if (invalid) {
      setErrorMsg(invalid);
      setToast({ show: true, message: invalid, type: "warning" });
      return;
    }
    if (saving) return;
  
    setSaving(true);
    try {
      // ✅ 1) คำนวณทุนสินค้าเปล่า (ยังไม่รวมของเสริม)
      const baseUnitCost = unitCost === "" ? 0 : Number(unitCost);
  
      // ✅ 2) id พวงกุญแจ (ถ้าไม่ใช้ ส่ง null)
      const keychainOptionId: string | null =
      useKeychain && selectedKeychain.id ? selectedKeychain.id : null;

      // ✅ 3) รายละเอียด accessory + จำนวนต่อชิ้น
      const accessoryUsages: AccessoryUsage[] =
      useAccessory && selectedAccessories.length > 0
        ? selectedAccessories.map((a) => ({
            id: a.id,
            qtyPerUnit: accessoryQtyById[a.id] ?? 1,
            extraCostPerUnit: a.extraCost,
          }))
        : [];

      const payload: MovementPayload = {
      product_code: productCode.trim(),
      qty: Number(qty),
      unit_cost: finalUnitCost,      // รวมทุกอย่าง
      base_unit_cost: baseUnitCost,  // ทุนตัวตุ๊กตาเปล่า
      keychain_option_id: keychainOptionId,
      accessory_ids: accessoryUsages.length > 0 ? accessoryUsages : null,
      reason: "PURCHASE",
      ref_table: "purchase",
      ref_no: refNo.trim() || "-",
      location_id: locationId,
      };
  
      const resp = await fetch(apiUrl("/inventory_movements"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
  
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        let msg = data?.error ?? resp.statusText;
      
        // ⬅️ เพิ่มส่วนนี้
        if (data?.error === "keychain_stock_not_enough") {
          const meta = data.meta;
          msg = `สต๊อกของพวงกุญแจ/เครื่องประดับไม่พอ (ID: ${meta?.keychainId}) ต้องการ ${meta?.requested} มีแค่ ${meta?.available}`;
        }
      
        setErrorMsg(msg);
        setToast({ show: true, message: msg, type: "danger" });
        return;
      }
      setToast({
        show: true,
        message: `เพิ่มสต๊อกให้ “${selectedProduct?.name ?? productCode}” สำเร็จ`,
        type: "success",
      });
      resetForm();

      // ดึงข้อมูลล่าสุด: สินค้า + พวงกุญแจ + เครื่องประดับ (เผื่อ stockOnHand/ต้นทุนเปลี่ยน)
      await refreshMasters();

    } catch (e: any) {
      const msg = e?.message ?? "เชื่อมต่อ API ไม่ได้ — ตรวจสอบเซิร์ฟเวอร์/เน็ตเวิร์ค";
      setErrorMsg(msg);
      setToast({ show: true, message: msg, type: "danger" });
    } finally {
      setSaving(false);
    }
  };

  // เช่น "PD12" -> { prefix: "PD", num: 12 }
  function splitCode(code: string): { prefix: string; num: number } {
    const m = String(code).trim().match(/^([A-Za-z]+)(\d+)$/);
    if (!m) return { prefix: code, num: Number.NaN };
    return { prefix: m[1], num: parseInt(m[2], 10) };
  }

  function naturalCodeCompare(a: Product, b: Product): number {
    const A = splitCode(a.code);
    const B = splitCode(b.code);
    const p = A.prefix.localeCompare(B.prefix, undefined, { sensitivity: "base" });
    if (p !== 0) return p;

    const aHas = Number.isFinite(A.num);
    const bHas = Number.isFinite(B.num);
    if (aHas && bHas) {
      if (A.num !== B.num) return A.num - B.num;
    } else if (aHas !== bHas) {
      return aHas ? -1 : 1;
    }

    return a.code.localeCompare(b.code, undefined, { sensitivity: "base" });
  }

  /** ---------- UI ---------- */
  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      {/* Toast มุมขวาบน */}
      <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
        {toast.show && (
          <div className={`alert alert-${toast.type ?? "success"} shadow mb-0 rounded-3 py-2 px-3`}>
            {toast.message}
          </div>
        )}
      </div>

      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h4 className="mb-0 text-primary">เพิ่มสต๊อกสินค้า (Stock In)</h4>

                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={refreshMasters}
                      disabled={loadingProducts || loadingKeychains}
                      title="ดึงข้อมูลสินค้า / พวงกุญแจ / เครื่องประดับจากฐานข้อมูลอีกครั้ง"
                    >
                      {loadingProducts || loadingKeychains ? "กำลังรีเฟรช…" : "รีเฟรช"}
                    </button>
                  </div>
                </div>

                <a
                  href="/ProductsAddPage"
                  className="btn btn-success btn-sm d-inline-flex align-items-center gap-2 mb-3"
                  data-bs-toggle="tooltip"
                  data-bs-placement="left"
                  title="ไปหน้าเพิ่มสินค้าใหม่"
                  aria-label="ไปหน้าเพิ่มสินค้าใหม่"
                >
                  <span className="fw-bold">+</span> เพิ่มสินค้าใหม่
                </a>

                {loadingProducts && (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <span>กำลังโหลดรายการสินค้า…</span>
                  </div>
                )}
                {productsError && (
                  <div className="alert alert-warning rounded-3" role="alert">
                    โหลดสินค้าไม่สำเร็จ: {productsError}
                  </div>
                )}

                {/* กล่องรายละเอียดสินค้า */}
                <div className="border rounded-3 p-3 bg-white mb-3">
                  <div className="mb-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <label htmlFor="productCode" className="form-label mb-0">
                        รหัสสินค้า
                      </label>

                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                        onClick={openProductsModal}
                        data-bs-toggle="tooltip"
                        data-bs-placement="left"
                        title="เปิดหน้าต่างรายการสินค้าทั้งหมด"
                        aria-label="เปิดหน้าต่างรายการสินค้าทั้งหมด"
                      >
                        ดูทั้งหมด
                      </button>
                    </div>
                    <input
                      id="productCode"
                      className="form-control"
                      list="productCodes"
                      placeholder={loadingProducts ? "กำลังโหลดสินค้า..." : "ค้นด้วยรหัสหรือเลือกจากรายการ"}
                      value={productCode}
                      disabled={loadingProducts}
                      onChange={(e) => setProductCode(e.target.value)}
                      onBlur={(e) => setProductCode(e.target.value.trim())}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          if (productsByCode.has(productCode) || productsByCode.has(productCode.toLowerCase())) {
                            (document.getElementById("qty") as HTMLInputElement | null)?.focus();
                          }
                        }
                      }}
                    />
                    <datalist id="productCodes">
                      {allProducts.map((p) => (
                        <option
                          key={p.code}
                          value={p.code}
                          label={`${p.code} — ${p.name}${p.unit ? ` • ${p.unit}` : ""}`}
                        />
                      ))}
                    </datalist>
                    {productCode && !selectedProduct && !loadingProducts && (
                      <div className="small text-danger mt-1">
                        ไม่พบรหัส <code>{productCode}</code> — กรุณาเพิ่มสินค้าในเมนู “เพิ่มสินค้าใหม่” ก่อน
                      </div>
                    )}
                    {selectedProduct && (
                      <div className="small text-muted mt-1">
                        ชื่อ: <span className="fw-semibold">{selectedProduct.name}</span>
                        {selectedProduct.unit && <> • หน่วย: {selectedProduct.unit}</>}
                      </div>
                    )}
                  </div>

                  {/* แถว: qty + ต้นทุนสินค้า (ไม่รวมพวงกุญแจ) */}
                  <div className="row g-3">
                    <div className="col-12 col-sm-6">
                      <label htmlFor="qty" className="form-label">
                        จำนวนที่รับเข้า (qty)
                      </label>
                      <input
                        id="qty"
                        type="number"
                        min={1}
                        step={1}
                        className="form-control"
                        value={qty}
                        onChange={(e) =>
                          setQty(e.target.value === "" ? "" : Math.max(1, Math.floor(Number(e.target.value) || 0)))
                        }
                      />
                    </div>
                    <div className="col-12 col-sm-6">
                      <label htmlFor="unitCost" className="form-label">
                        ต้นทุนสินค้าต่อหน่วย (ไม่รวมพวงกุญแจ)
                      </label>
                      <input
                        id="unitCost"
                        type="number"
                        min={0}
                        step="0.01"
                        className="form-control"
                        value={unitCost}
                        onChange={(e) =>
                          setUnitCost(e.target.value === "" ? "" : Math.max(0, Number(e.target.value)))
                        }
                      />
                    </div>
                  </div>

                  {/* แถว: พวงกุญแจ + ต้นทุนพวงกุญแจ + รวมต้นทุน */}
                  <div className="row g-3 mt-2">
                    <div className="col-12 col-sm-6">
                      <label className="form-label d-flex justify-content-between align-items-center">
                        <span>พวงกุญแจ</span>
                        <div className="form-check form-switch mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="useKeychainSwitch"
                            checked={useKeychain}
                            onChange={(e) => setUseKeychain(e.target.checked)}
                          />
                          <label
                            className="form-check-label small"
                            htmlFor="useKeychainSwitch"
                          >
                            ใช้พวงกุญแจ
                          </label>
                        </div>
                      </label>

        

                      {/* ⭐ แสดงเฉพาะเมื่อเปิดสวิตช์เท่านั้น */}
                      {useKeychain && (
                        <>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm mb-2"
                            onClick={openKeychainModal}
                          >
                            เพิ่มพวงกุญแจ
                          </button>

                          <div className="border rounded-3 p-2 bg-light">
                            <div className="fw-semibold">{selectedKeychain.label}</div>
                            <div className="small text-muted">
                              ต้นทุนเพิ่มต่อชิ้น: {keychainUnitCost.toFixed(2)} บาท
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="col-12 col-sm-6">
                      <label className="form-label">
                        รวมต้นทุนสินค้าต่อชิ้น (รวมพวงกุญแจถ้าเปิดใช้งาน)
                      </label>
                      <input
                        className="form-control"
                        value={Number.isFinite(finalUnitCost) ? finalUnitCost.toFixed(2) : ""}
                        readOnly
                      />
                      <div className="form-text">
                        = ต้นทุนสินค้า + ต้นทุนพวงกุญแจ (เฉพาะตอนเปิดใช้)
                      </div>
                    </div>
                  </div>

                  {/* แถว: เครื่องประดับ */}
                  <div className="row g-3 mt-2">
                    <div className="col-12 col-sm-6">
                      <label className="form-label d-flex justify-content-between align-items-center">
                        <span>เครื่องประดับเพิ่ม</span>
                        <div className="form-check form-switch mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="useAccessorySwitch"
                            checked={useAccessory}
                            onChange={(e) => setUseAccessory(e.target.checked)}
                          />
                          <label
                            className="form-check-label small"
                            htmlFor="useAccessorySwitch"
                          >
                            ใช้เครื่องประดับ
                          </label>
                        </div>
                      </label>

                      {useAccessory && (
                        <>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm mb-2"
                            onClick={openAccessoryModal}
                          >
                            เลือกเครื่องประดับ
                          </button>

                          <div className="border rounded-3 p-2 bg-light">
                            {selectedAccessories.length === 0 ? (
                              <div className="small text-muted">
                                ยังไม่ได้เลือกเครื่องประดับ
                              </div>
                            ) : (
                              <>
                                <div className="small fw-semibold mb-1">
                                  เลือกแล้ว {selectedAccessories.length} รายการ
                                </div>
                                <ul className="small mb-1 ps-3">
                                  {selectedAccessories.map((a) => {
                                    const qtyPerUnit = accessoryQtyById[a.id] ?? 1;
                                    return (
                                      <li key={a.id}>
                                        {a.label} (฿{a.extraCost.toFixed(2)} × {qtyPerUnit} ชิ้น)
                                      </li>
                                    );
                                  })}
                                </ul>
                                <div className="small text-muted">
                                  รวมต้นทุนเครื่องประดับต่อชิ้น:{" "}
                                  {accessoryExtraCost.toFixed(2)} บาท
                                </div>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* -------- Modal: เลือกเครื่องประดับ -------- */}
                  {showAccessoryModal && (
                    <div
                      className="modal fade show"
                      role="dialog"
                      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
                      aria-modal="true"
                      aria-labelledby="accessoryModalLabel"
                      onClick={(e) => {
                        if (e.target === e.currentTarget) closeAccessoryModal();
                      }}
                    >
                      <div className="modal-dialog modal-md modal-dialog-scrollable">
                        <div className="modal-content rounded-4 shadow">
                          <div className="modal-header">
                            <h5 className="modal-title" id="accessoryModalLabel">
                              เลือกเครื่องประดับ
                            </h5>
                            <button
                              type="button"
                              className="btn-close"
                              aria-label="Close"
                              onClick={closeAccessoryModal}
                            />
                          </div>

                          <div className="modal-body">
                            <div className="input-group mb-3">
                              <span className="input-group-text">ค้นหา</span>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="พิมพ์ชื่อเครื่องประดับ"
                                value={accessoryQuery}
                                onChange={(e) => setAccessoryQuery(e.target.value)}
                                autoFocus
                              />
                            </div>

                            <div className="table-responsive">
                              <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th style={{ width: 40 }}></th>
                                    <th style={{ width: 64 }}>รูป</th>
                                    <th>ตัวเลือก</th>
                                    <th className="text-center" style={{ width: 80 }}>
                                      คงเหลือ
                                    </th>
                                    <th className="text-center" style={{ width: 90 }}>
                                      จำนวน/ชิ้น
                                    </th>
                                    <th className="text-end" style={{ width: 140 }}>
                                      ต้นทุนต่อชิ้น
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredAccessoryOptions.map((a) => {
                                    const disabled = !a.stockOnHand || a.stockOnHand <= 0;
                                    const checked = selectedAccessoryIds.includes(a.id);
                                    const qtyPerUnit = accessoryQtyById[a.id] ?? 1;

                                    return (
                                      <tr
                                        key={a.id}
                                        role="button"
                                        onClick={() => {
                                          if (!disabled) toggleAccessory(a);
                                        }}
                                        className={
                                          "user-select-none " +
                                          (disabled ? "opacity-50" : "table-row-hover")
                                        }
                                        style={{
                                          cursor: disabled ? "not-allowed" : "pointer",
                                        }}
                                      >
                                        <td className="text-center">
                                          <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={checked}
                                            readOnly
                                          />
                                        </td>
                                        <td>
                                          {a.imageUrl ? (
                                            <img
                                              src={a.imageUrl}
                                              alt={a.label}
                                              width={40}
                                              height={40}
                                              style={{
                                                objectFit: "cover",
                                                borderRadius: 8,
                                                background: "#f5f5f5",
                                              }}
                                            />
                                          ) : (
                                            <div
                                              className="bg-light border rounded"
                                              style={{ width: 40, height: 40 }}
                                            />
                                          )}
                                        </td>
                                        <td>{a.label}</td>
                                        <td className="text-center">
                                          {a.stockOnHand != null
                                            ? a.stockOnHand.toLocaleString("th-TH")
                                            : "-"}
                                        </td>

                                        {/* 👉 ช่องจำนวน/ชิ้น */}
                                        <td
                                          className="text-center"
                                          onClick={(e) => e.stopPropagation()} // กันไม่ให้คลิกแล้ว toggle แถว
                                        >
                                          {checked ? (
                                            <input
                                              type="number"
                                              min={1}
                                              className="form-control form-control-sm text-center"
                                              value={qtyPerUnit}
                                              onChange={(e) =>
                                                setAccessoryQty(a.id, Number(e.target.value) || 1)
                                              }
                                            />
                                          ) : (
                                            <span className="text-muted">-</span>
                                          )}
                                        </td>

                                        <td className="text-end">
                                          {a.extraCost.toFixed(2)} บาท
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <div className="modal-footer">
                            <div className="me-auto small text-muted">
                              รวมต้นทุนเครื่องประดับต่อชิ้น:{" "}
                              {accessoryExtraCost.toFixed(2)} บาท
                            </div>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={closeAccessoryModal}
                            >
                              ปิดหน้าต่าง
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-3">
                    <label className="form-label">รับเข้า Location</label>
                    <div className="d-flex gap-2 flex-wrap">
                      {/* PMN-CRITICAL(/StockInPage): location ids are hardcoded to DB inventory_locations ids; update PMN/backend if DB ids change. */}
                      {[
                        { id: 1, label: "MAIN" },
                        { id: 2, label: "SHOP" },
                        { id: 3, label: "TIKTOK" },
                        { id: 4, label: "USA" },
                      ].map((loc) => (
                        <button
                          key={loc.id}
                          type="button"
                          className={`btn btn-sm ${locationId === loc.id ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => setLocationId(loc.id)}
                        >
                          {loc.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-3">
                    <label htmlFor="refNo" className="form-label">
                      เลขอ้างอิง (Ref No.)
                    </label>
                    <input
                      id="refNo"
                      type="text"
                      className="form-control"
                      placeholder="เช่น PO-001 / ใบรับของ"
                      value={refNo}
                      onChange={(e) => setRefNo(e.target.value)}
                    />
                    <div className="form-text">
                      เหตุผลจะตั้งเป็น <code>PURCHASE</code> และตารางอ้างอิงเป็น <code>purchase</code> อัตโนมัติ
                    </div>
                  </div>
                </div>

                {errorMsg && (
                  <div className="alert alert-warning rounded-3" role="alert">
                    <div className="fw-semibold">ข้อผิดพลาด:</div>
                    <div className="text-break">{errorMsg}</div>
                  </div>
                )}

                <div className="d-grid">
                  <button
                    type="button"
                    className="btn btn-primary btn-lg"
                    disabled={saving}
                    onClick={submit}
                  >
                    {saving ? "กำลังบันทึก…" : "💾 บันทึกการรับเข้าสต๊อก"}
                  </button>
                </div>

                <div className="d-flex gap-2 justify-content-between mt-3">
                  <button type="button" className="btn btn-outline-secondary flex-fill" onClick={resetForm}>
                    ล้างฟอร์ม
                  </button>
                  <a href="/" className="btn btn-link text-decoration-none flex-fill text-center">
                    ← กลับหน้าเมนู
                  </a>
                </div>
              </div>
            </div>

            <div className="text-center small text-muted mt-3">
              * แบบฟอร์มนี้ใช้รูปแบบ UI/การ์ด/โทนสีเดียวกับหน้า Scan เพื่อให้สอดคล้องกัน
            </div>
          </div>
        </div>

        {/* -------- Modal: รายการสินค้า -------- */}
        {showProductsModal && (
          <div
            className="modal fade show"
            role="dialog"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,.5)" }}
            aria-modal="true"
            aria-labelledby="productsModalLabel"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeProductsModal();
            }}
          >
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content rounded-4 shadow">
                <div className="modal-header">
                  <h5 className="modal-title" id="productsModalLabel">
                    รายการสินค้าทั้งหมด
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={closeProductsModal}
                  />
                </div>

                <div className="modal-body">
                  <div className="input-group mb-3">
                    <span className="input-group-text">ค้นหา</span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="พิมพ์โค้ด / ชื่อ / หน่วย"
                      value={productQuery}
                      onChange={(e) => setProductQuery(e.target.value)}
                      autoFocus
                    />
                  </div>

                  <div className="table-responsive" style={{ maxHeight: 420 }}>
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th style={{ width: 72 }}>รูป</th>
                          <th style={{ width: 140 }}>โค้ด</th>
                          <th>ชื่อสินค้า</th>
                          <th style={{ width: 120 }} className="text-end">
                            ราคา
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProducts.map((p) => (
                          <tr
                            key={p.code}
                            role="button"
                            onClick={() => chooseProduct(p)}
                            className="user-select-none"
                          >
                            <td>
                              {p.imageUrl ? (
                                <img
                                  src={p.imageUrl}
                                  alt={p.name}
                                  width={56}
                                  height={56}
                                  style={{
                                    objectFit: "cover",
                                    borderRadius: 8,
                                    background: "#f5f5f5",
                                    cursor: "zoom-in",
                                    transition: "transform 0.2s ease",
                                  }}
                                  onMouseEnter={() => setPreviewImage(p.imageUrl ?? null)}
                                  onMouseLeave={() => setPreviewImage(null)}
                                  onError={(e) => {
                                    const img = e.currentTarget;
                                    img.onerror = null;
                                    img.src = "/no-image.png";
                                  }}
                                />
                              ) : (
                                <div
                                  className="bg-light border rounded"
                                  style={{ width: 56, height: 56 }}
                                />
                              )}
                            </td>
                            <td className="fw-semibold">{p.code}</td>
                            <td>{p.name}</td>
                            <td className="text-end">
                              {p.price.toLocaleString("th-TH", {
                                style: "currency",
                                currency: "THB",
                              })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeProductsModal}>
                    ปิดหน้าต่าง
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* -------- Modal: เลือกพวงกุญแจ -------- */}
        {showKeychainModal && (
          <div
            className="modal fade show"
            role="dialog"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            aria-modal="true"
            aria-labelledby="keychainModalLabel"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeKeychainModal();
            }}
          >
            <div className="modal-dialog modal-md modal-dialog-scrollable">
              <div className="modal-content rounded-4 shadow">
                <div className="modal-header">
                  <h5 className="modal-title" id="keychainModalLabel">
                    เลือกพวงกุญแจ
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={closeKeychainModal}
                  />
                </div>

                <div className="modal-body">
                  <div className="input-group mb-3">
                    <span className="input-group-text">ค้นหา</span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="พิมพ์ชื่อพวงกุญแจ"
                      value={keychainQuery}
                      onChange={(e) => setKeychainQuery(e.target.value)}
                      autoFocus
                    />
                  </div>

                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 64 }}>รูป</th>
                          <th>ตัวเลือก</th>
                          <th className="text-center" style={{ width: 80 }}>
                            คงเหลือ
                          </th>
                          <th className="text-end" style={{ width: 140 }}>
                            ต้นทุนเพิ่มต่อชิ้น
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredKeychainOptions.map((k) => {
                          const disabled = !k.stockOnHand || k.stockOnHand <= 0;

                          return (
                            <tr
                              key={k.id}
                              role="button"
                              onClick={() => {
                                if (!disabled) chooseKeychain(k);
                              }}
                              className={
                                "user-select-none " + (disabled ? "opacity-50" : "table-row-hover")
                              }
                              style={{
                                cursor: disabled ? "not-allowed" : "pointer",
                                pointerEvents: disabled ? "none" : "auto",
                              }}
                            >
                              <td>
                                {k.imageUrl ? (
                                  <img
                                    src={k.imageUrl}
                                    alt={k.label}
                                    width={40}
                                    height={40}
                                    style={{
                                      objectFit: "cover",
                                      borderRadius: 8,
                                      background: "#f5f5f5",
                                    }}
                                  />
                                ) : (
                                  <div
                                    className="bg-light border rounded"
                                    style={{ width: 40, height: 40 }}
                                  />
                                )}
                              </td>

                              <td>{k.label}</td>

                              <td className="text-center">
                                {k.stockOnHand != null
                                  ? k.stockOnHand.toLocaleString("th-TH")
                                  : "-"}
                              </td>

                              <td className="text-end">
                                {k.extraCost.toFixed(2)} บาท
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeKeychainModal}>
                    ปิดหน้าต่าง
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {previewImage && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{
              backgroundColor: "rgba(0,0,0,0.7)",
              zIndex: 2000,
              pointerEvents: "none",
            }}
          >
            <img
              src={previewImage}
              alt="preview"
              style={{
                maxWidth: "90%",
                maxHeight: "90%",
                borderRadius: 12,
                boxShadow: "0 0 20px rgba(0,0,0,0.5)",
                background: "#fff",
                transition: "transform 0.3s ease",
                transform: "scale(1.05)",
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
